import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  RefreshCw,
  Info,
  DollarSign,
  Search,
  ChevronDown,
  X,
  AlertTriangle,
  CheckCircle,
  Clock,
  Package,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  Zap,
  Shield,
  Smartphone,
  DollarSign as Dollar,
  ArrowRightLeft
} from 'lucide-react';
import { useDatabase } from '../hooks/useDatabase';
import { useMarketData } from '../contexts/MarketDataContext';
import { Editorial, BorderRadius, Typography } from '../styles/primitives';

interface SwapCryptoPageProps {
  usdtBalance: number;
  btcBalance: number;
  currentBtcPrice: number;
  onSwap: (fromCurrency: string, toCurrency: string, amount: number, toAmountReceived?: number) => Promise<boolean>;
  availableBalance?: number;
  userAssets?: DatabaseUserAsset[];
  fetchTransactions?: () => Promise<void>;
}

interface CryptoCurrency {
  symbol: string;
  name: string;
  iconUrl: string;
  balance?: number;
  price?: number;
}

// Define icon URLs for swap symbols
const CRYPTO_ICON_URLS: Record<string, string> = {
  BTC: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
  ETH: 'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
  USDT: 'https://assets.coingecko.com/coins/images/325/large/Tether.png',
  USDC: 'https://assets.coingecko.com/coins/images/6319/large/USD_Coin_icon.png',
  BNB: 'https://assets.coingecko.com/coins/images/825/large/bnb-icon2_2x.png',
  SOL: 'https://assets.coingecko.com/coins/images/4128/large/solana.png',
  XRP: 'https://assets.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png',
  ADA: 'https://assets.coingecko.com/coins/images/975/large/cardano.png',
  DOGE: 'https://assets.coingecko.com/coins/images/5/large/dogecoin.png',
  AVAX: 'https://assets.coingecko.com/coins/images/12559/large/Avalanche_Circle_RedWhite_Trans.png',
  MATIC: 'https://assets.coingecko.com/coins/images/4713/large/matic-token-icon.png',
  TRX: 'https://assets.coingecko.com/coins/images/1094/large/tron-logo.png',
  DOT: 'https://assets.coingecko.com/coins/images/12171/large/polkadot.png',
  SHIB: 'https://assets.coingecko.com/coins/images/11939/large/shiba.png',
  TON: 'https://assets.coingecko.com/coins/images/17980/large/ton_symbol.png',
  APT: 'https://assets.coingecko.com/coins/images/26455/large/aptos_round.png',
  ARB: 'https://assets.coingecko.com/coins/images/16547/large/photo_2023-03-29_21.47.00.jpeg',
  OP: 'https://assets.coingecko.com/coins/images/25244/large/Optimism.png',
  LTC: 'https://assets.coingecko.com/coins/images/2/large/litecoin.png',
  PEPE: 'https://assets.coingecko.com/coins/images/29850/large/pepe-token.jpeg'
};

// Define the allowed swap symbols
const ALLOWED_SWAP_SYMBOLS = [
  { symbol: 'BTC', name: 'Bitcoin' },
  { symbol: 'ETH', name: 'Ethereum' },
  { symbol: 'USDT', name: 'Tether' },
  { symbol: 'USDC', name: 'USD Coin' },
  { symbol: 'BNB', name: 'BNB' },
  { symbol: 'SOL', name: 'Solana' },
  { symbol: 'XRP', name: 'XRP' },
  { symbol: 'ADA', name: 'Cardano' },
  { symbol: 'DOGE', name: 'Dogecoin' },
  { symbol: 'AVAX', name: 'Avalanche' },
  { symbol: 'MATIC', name: 'Polygon' },
  { symbol: 'TRX', name: 'TRON' },
  { symbol: 'DOT', name: 'Polkadot' },
  { symbol: 'SHIB', name: 'Shiba Inu' },
  { symbol: 'TON', name: 'Toncoin' },
  { symbol: 'APT', name: 'Aptos' },
  { symbol: 'ARB', name: 'Arbitrum' },
  { symbol: 'OP', name: 'Optimism' },
  { symbol: 'LTC', name: 'Litecoin' },
  { symbol: 'PEPE', name: 'PEPE' }
];

const SwapCryptoPage: React.FC<SwapCryptoPageProps> = ({
  usdtBalance,
  btcBalance,
  currentBtcPrice,
  onSwap,
  availableBalance,
  userAssets = [],
  fetchTransactions
}) => {
  const { t } = useTranslation();
  const { transactions } = useDatabase();
  const { marketData, snapshotData, isConnected: isLiveDataConnected, getSnapshotPriceBySymbol, refreshSnapshot, lastSnapshotTime } = useMarketData();

  // Helper function to get price for any symbol with multiple fallback strategies
  const getPriceForSymbol = useCallback((symbol: string): number => {
    // Stablecoins always return 1
    if (symbol === 'USDT' || symbol === 'USDC') {
      return 1;
    }

    const tradingPair = `${symbol}USDT`;

    // Strategy 1: Try snapshot data first (most stable)
    const snapshotPrice = getSnapshotPriceBySymbol(tradingPair);
    if (snapshotPrice > 0) {
      return snapshotPrice;
    }

    // Strategy 2: Try direct marketData lookup
    const marketDataItem = marketData.find(item => item.symbol === tradingPair);
    if (marketDataItem && marketDataItem.price > 0) {
      return marketDataItem.price;
    }

    // Strategy 3: Try snapshotData direct lookup
    const snapshotItem = snapshotData.find(item => item.symbol === tradingPair);
    if (snapshotItem && snapshotItem.price > 0) {
      return snapshotItem.price;
    }

    // Strategy 4: For BTC, use the prop price as final fallback
    if (symbol === 'BTC' && currentBtcPrice > 0) {
      return currentBtcPrice;
    }

    return 0;
  }, [getSnapshotPriceBySymbol, marketData, snapshotData, currentBtcPrice]);
  
  // State for swap form
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [calculatedToAmountFullPrecision, setCalculatedToAmountFullPrecision] = useState<number | null>(null);
  const [calculatedFromAmountFullPrecision, setCalculatedFromAmountFullPrecision] = useState<number | null>(null);
  const [fromCurrency, setFromCurrency] = useState<CryptoCurrency>(() => ({
    symbol: 'USDT',
    name: 'Tether',
    iconUrl: 'https://assets.coingecko.com/coins/images/325/large/Tether.png',
    balance: usdtBalance,
    price: 1
  }));
  const [toCurrency, setToCurrency] = useState<CryptoCurrency>(() => ({
    symbol: 'BTC',
    name: 'Bitcoin',
    iconUrl: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
    balance: btcBalance,
    price: 0 // Will be set by useEffect
  }));
  
  // State for token selection
  const [showFromTokens, setShowFromTokens] = useState(false);
  const [showToTokens, setShowToTokens] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState<CryptoCurrency[]>([]);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false);
  
  // State for swap status
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);
  const [swapSuccess, setSwapSuccess] = useState<string | null>(null);
  
  // State for confirmation modal
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // State for order history
  const [activeSubTab, setActiveSubTab] = useState<'markets' | 'my_orders'>('markets');
  
  // Refs for dropdown handling
  const fromDropdownRef = useRef<HTMLDivElement>(null);
  const toDropdownRef = useRef<HTMLDivElement>(null);

  // Set up interval to refresh market data every minute when on swap page
  useEffect(() => {
    // Refresh snapshot to get latest prices
    refreshSnapshot();

    // Set up interval to refresh snapshot every minute
    const intervalId = setInterval(() => {
      console.log('Refreshing market data for swap page...');
      refreshSnapshot();
    }, 60000); // 60 seconds = 1 minute

    // Clean up interval on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [refreshSnapshot]);

  // Update currency balances and prices when market data changes
  useEffect(() => {
    setFromCurrency(prev => {
      const userAsset = userAssets.find(asset => asset.asset_symbol === prev.symbol);
      const balance = userAsset ? userAsset.balance :
                     prev.symbol === 'USDT' ? usdtBalance :
                     prev.symbol === 'BTC' ? btcBalance :
                     prev.balance;

      const price = getPriceForSymbol(prev.symbol);

      return { ...prev, balance, price };
    });

    setToCurrency(prev => {
      const userAsset = userAssets.find(asset => asset.asset_symbol === prev.symbol);
      const balance = userAsset ? userAsset.balance :
                     prev.symbol === 'USDT' ? usdtBalance :
                     prev.symbol === 'BTC' ? btcBalance :
                     prev.balance;

      const price = getPriceForSymbol(prev.symbol);

      return { ...prev, balance, price };
    });

    // Also update in available currencies
    setAvailableCurrencies(prev =>
      prev.map(currency => {
        const userAsset = userAssets.find(asset => asset.asset_symbol === currency.symbol);
        const balance = userAsset ? userAsset.balance :
                       currency.symbol === 'USDT' ? usdtBalance :
                       currency.symbol === 'BTC' ? btcBalance :
                       currency.balance;

        const price = getPriceForSymbol(currency.symbol);

        return { ...currency, balance, price };
      })
    );
  }, [usdtBalance, btcBalance, userAssets, getPriceForSymbol, marketData, snapshotData, lastSnapshotTime]);

  // Initialize available currencies
  useEffect(() => {
    setIsLoadingCurrencies(true);

    // Create initial currencies array based on ALLOWED_SWAP_SYMBOLS
    let initialCurrencies: CryptoCurrency[] = [];

    // Process each allowed symbol
    for (const allowedCrypto of ALLOWED_SWAP_SYMBOLS) {
      const symbol = allowedCrypto.symbol;
      const name = allowedCrypto.name;

      // Get icon URL from static mapping
      const iconUrl = CRYPTO_ICON_URLS[symbol] || 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png';

      // Get price using the robust helper function
      const price = getPriceForSymbol(symbol);

      // Get balance from user assets or default balances
      let balance;
      const userAsset = userAssets.find(asset => asset.asset_symbol === symbol);
      if (userAsset) {
        balance = userAsset.balance;
      } else if (symbol === 'BTC') {
        balance = btcBalance;
      } else if (symbol === 'USDT') {
        balance = usdtBalance;
      }

      // Add to available currencies
      initialCurrencies.push({
        symbol,
        name,
        iconUrl,
        price,
        balance
      });
    }

    setAvailableCurrencies(initialCurrencies);
    setIsLoadingCurrencies(false);
  }, [marketData, snapshotData, usdtBalance, btcBalance, userAssets, getPriceForSymbol]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target as Node)) {
        setShowFromTokens(false);
      }
      if (toDropdownRef.current && !toDropdownRef.current.contains(event.target as Node)) {
        setShowToTokens(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear error/success messages after 5 seconds
  useEffect(() => {
    if (swapError || swapSuccess) {
      const timer = setTimeout(() => {
        setSwapError(null);
        setSwapSuccess(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [swapError, swapSuccess]);

  // Calculate exchange rate
  const getExchangeRate = () => {
    if (fromCurrency.symbol === 'USDT' && toCurrency.symbol === 'BTC') {
      return availableBalance !== undefined ? availableBalance : currentBtcPrice;
    }
    if (fromCurrency.symbol === 'BTC' && toCurrency.symbol === 'USDT') {
      return availableBalance || usdtBalance;
    }
    if (fromCurrency.price && toCurrency.price) {
      return fromCurrency.price / toCurrency.price;
    }
    return 0;
  };

  // Handle from amount change
  const handleFromAmountChange = (value: string) => {
    // Sanitize input - only allow numbers and decimal point
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const decimalCount = (sanitizedValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }
    
    // Limit decimal places to 8
    const parts = sanitizedValue.split('.');
    if (parts[1] && parts[1].length > 8) {
      return;
    }
    
    setFromAmount(sanitizedValue);

    // Calculate to amount based on exchange rate
    const numValue = parseFloat(sanitizedValue);
    if (!isNaN(numValue) && numValue > 0) {
      if (!fromCurrency.price || fromCurrency.price <= 0) {
        console.error(`SwapPage: Missing price for ${fromCurrency.symbol}`);
        setSwapError(`Unable to get current price for ${fromCurrency.symbol}. Please refresh prices.`);
        setCalculatedToAmountFullPrecision(null);
        setToAmount('');
        return;
      }

      if (!toCurrency.price || toCurrency.price <= 0) {
        console.error(`SwapPage: Missing price for ${toCurrency.symbol}`);
        setSwapError(`Unable to get current price for ${toCurrency.symbol}. Please refresh prices.`);
        setCalculatedToAmountFullPrecision(null);
        setToAmount('');
        return;
      }

      // Clear any previous errors
      setSwapError(null);

      const SWAP_FEE_RATE = 0.001; // 0.1% fee
      const exchangeRate = fromCurrency.price / toCurrency.price;
      const calculatedToAmount = numValue * exchangeRate * (1 - SWAP_FEE_RATE);

      // Validate the calculated amount is reasonable
      if (!isNaN(calculatedToAmount) && isFinite(calculatedToAmount)) {
        // Store full precision for slippage calculation
        setCalculatedToAmountFullPrecision(calculatedToAmount);
        // Store rounded version for display
        setToAmount(calculatedToAmount.toFixed(8));
      } else {
        setCalculatedToAmountFullPrecision(null);
        setToAmount('');
      }
    } else {
      setCalculatedToAmountFullPrecision(null);
      setToAmount('');
    }
  };

  // Handle to amount change
  const handleToAmountChange = (value: string) => {
    // Sanitize input - only allow numbers and decimal point
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const decimalCount = (sanitizedValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }
    
    // Limit decimal places to 8
    const parts = sanitizedValue.split('.');
    if (parts[1] && parts[1].length > 8) {
      return;
    }
    
    setToAmount(sanitizedValue);

    // Calculate from amount based on exchange rate
    const numValue = parseFloat(sanitizedValue);
    if (!isNaN(numValue) && numValue > 0) {
      if (!fromCurrency.price || fromCurrency.price <= 0) {
        console.error(`SwapPage: Missing price for ${fromCurrency.symbol}`);
        setSwapError(`Unable to get current price for ${fromCurrency.symbol}. Please refresh prices.`);
        setCalculatedFromAmountFullPrecision(null);
        setFromAmount('');
        return;
      }

      if (!toCurrency.price || toCurrency.price <= 0) {
        console.error(`SwapPage: Missing price for ${toCurrency.symbol}`);
        setSwapError(`Unable to get current price for ${toCurrency.symbol}. Please refresh prices.`);
        setCalculatedFromAmountFullPrecision(null);
        setFromAmount('');
        return;
      }

      // Clear any previous errors
      setSwapError(null);

      const SWAP_FEE_RATE = 0.001; // 0.1% fee
      const exchangeRate = toCurrency.price / fromCurrency.price;
      const calculatedFromAmount = numValue * exchangeRate / (1 - SWAP_FEE_RATE);

      // Validate the calculated amount is reasonable
      if (!isNaN(calculatedFromAmount) && isFinite(calculatedFromAmount)) {
        // Store full precision for slippage calculation
        setCalculatedFromAmountFullPrecision(calculatedFromAmount);
        // Store rounded version for display
        setFromAmount(calculatedFromAmount.toFixed(8));
      } else {
        setCalculatedFromAmountFullPrecision(null);
        setFromAmount('');
      }
    } else {
      setCalculatedFromAmountFullPrecision(null);
      setFromAmount('');
    }
  };

  // Swap the currencies
  const handleSwapCurrencies = () => {
    const tempCurrency = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(tempCurrency);
    
    // Also swap the amounts
    const tempAmount = fromAmount;
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  // Select a currency for the "from" field
  const handleSelectFromCurrency = (currency: CryptoCurrency) => {
    if (currency.symbol === toCurrency.symbol) {
      // If selecting the same currency as "to", swap them
      setFromCurrency(toCurrency);
      setToCurrency(currency);
    } else {
      setFromCurrency(currency);
    }
    
    setShowFromTokens(false);
    setSearchTerm('');
    
    // Recalculate amounts
    if (fromAmount) {
      handleFromAmountChange(fromAmount);
    }
  };

  // Select a currency for the "to" field
  const handleSelectToCurrency = (currency: CryptoCurrency) => {
    if (currency.symbol === fromCurrency.symbol) {
      // If selecting the same currency as "from", swap them
      setToCurrency(fromCurrency);
      setFromCurrency(currency);
    } else {
      setToCurrency(currency);
    }
    
    setShowToTokens(false);
    setSearchTerm('');
    
    // Recalculate amounts
    if (toAmount) {
      handleToAmountChange(toAmount);
    }
  };

  // Filter tokens based on search term
  const getFilteredTokens = (term: string) => {
    if (!term) return availableCurrencies;
    
    const lowerTerm = term.toLowerCase();
    return availableCurrencies.filter(currency => 
      currency.symbol.toLowerCase().includes(lowerTerm) || 
      currency.name.toLowerCase().includes(lowerTerm)
    );
  };

  // Handle max button click
  const handleMaxClick = () => {
    if (fromCurrency.balance) {
      // Define swap fee rate as a constant
      const SWAP_FEE_RATE = 0.001; // 0.1% fee
      
      // For the max button, we want to use the full balance minus a small buffer
      // The fee is deducted from the output, not added to the input
      const buffer = fromCurrency.balance * 0.001; // 0.1% buffer for safety
      const maxSwappable = Math.max(0, fromCurrency.balance - buffer);
      
      // Round down to appropriate decimal places to prevent floating-point inaccuracies
      const decimals = fromCurrency.symbol === 'USDT' ? 2 : 6;
      const maxSwappableValue = Math.floor(maxSwappable * Math.pow(10, decimals)) / Math.pow(10, decimals);
      const maxSwappableString = maxSwappableValue.toFixed(decimals);
      
      // Additional validation
      if (maxSwappableValue <= 0) {
        setSwapError('Insufficient balance for swap');
        return;
      }
      
      setFromAmount(maxSwappableString);
      handleFromAmountChange(maxSwappableString);
    }
  };

  // Show confirmation modal
  const handleShowConfirmation = () => {
    // Validate input
    const amount = parseFloat(fromAmount);
    if (isNaN(amount) || amount <= 0) {
      setSwapError('Please enter a valid amount');
      return;
    }
    
    // Additional pre-confirmation checks
    const MIN_SWAP_AMOUNT = 0.0001;
    const MAX_SWAP_AMOUNT_USD = 1000000;
    const MAX_SLIPPAGE = 0.05; // 5% maximum slippage
    
    if (amount < MIN_SWAP_AMOUNT) {
      setSwapError(`Minimum swap amount is ${MIN_SWAP_AMOUNT}`);
      return;
    }
    
    // Check USD value limit
    const fromUsdValue = amount * (fromCurrency.price || 0);
    if (fromUsdValue > MAX_SWAP_AMOUNT_USD) {
      setSwapError(`Maximum swap amount is $${MAX_SWAP_AMOUNT_USD.toLocaleString()}`);
      return;
    }
    
    // Check balance
    if (fromCurrency.balance && amount > fromCurrency.balance) {
      setSwapError(`Insufficient ${fromCurrency.symbol} balance`);
      return;
    }
    
    // Validate currencies are different
    if (fromCurrency.symbol === toCurrency.symbol) {
      setSwapError('Cannot swap to the same currency');
      return;
    }
    
    // Validate prices are available and reasonable
    if (!fromCurrency.price || !toCurrency.price || fromCurrency.price <= 0 || toCurrency.price <= 0) {
      setSwapError('Currency prices unavailable. Please try again later.');
      return;
    }
    
    // Validate the to amount is calculated and reasonable
    const toAmountNum = parseFloat(toAmount);
    if (isNaN(toAmountNum) || toAmountNum <= 0) {
      setSwapError('Invalid amount to receive. Please try again.');
      return;
    }
    
    // Enhanced slippage validation using full precision values
    const SWAP_FEE_RATE = 0.001; // 0.1% fee
    const expectedExchangeRate = fromCurrency.price / toCurrency.price;
    const expectedToAmount = amount * expectedExchangeRate * (1 - SWAP_FEE_RATE);
    
    // Use full precision value if available, otherwise recalculate
    const actualToAmount = calculatedToAmountFullPrecision !== null ? calculatedToAmountFullPrecision : expectedToAmount;
    
    // Prevent division by zero or extremely small numbers
    if (expectedToAmount <= 0.00000001) { // Less than 1 satoshi equivalent
      setSwapError('Calculated output amount is too small. Please increase the input amount.');
      return;
    }
    
    // Calculate slippage with safeguards
    const slippage = Math.abs(actualToAmount - expectedToAmount) / expectedToAmount;
    
    // Additional sanity check: if slippage is unreasonably high, it's likely a calculation error
    if (slippage > 1.0) { // More than 100% slippage indicates a serious error
      setSwapError('Price calculation error detected. Please refresh the page and try again.');
      return;
    }
    
    if (slippage > MAX_SLIPPAGE) {
      setSwapError(`Price has changed too much (${(slippage * 100).toFixed(2)}% slippage). Please try again.`);
      return;
    }
    
    // Validate the swap ratio is reasonable (prevent extreme ratios)
    const swapRatio = actualToAmount / amount;
    const expectedRatio = expectedExchangeRate * (1 - SWAP_FEE_RATE);
    const ratioDifference = Math.abs(swapRatio - expectedRatio) / Math.max(expectedRatio, 0.00000001);
    
    if (ratioDifference > MAX_SLIPPAGE) {
      setSwapError('Swap calculation error detected. Please try again.');
      return;
    }
    
    // Additional security: ensure we're not giving out more than what's reasonable
    const maxReasonableToAmount = amount * 1000; // No more than 1000x the input amount
    if (actualToAmount > maxReasonableToAmount) {
      setSwapError('Calculated output amount is unreasonably high. Please check the swap parameters.');
      return;
    }
    
    setSwapError(null);
    setShowConfirmation(true);
  };

  // Execute the swap after confirmation
  const executeSwap = async () => {
    const amount = parseFloat(fromAmount);
    
    // Use full precision value for execution, or recalculate if not available
    let toAmountNum = calculatedToAmountFullPrecision;
    if (toAmountNum === null || isNaN(toAmountNum)) {
      // Recalculate with full precision
      const SWAP_FEE_RATE = 0.001;
      const exchangeRate = (fromCurrency.price || 0) / (toCurrency.price || 0);
      toAmountNum = amount * exchangeRate * (1 - SWAP_FEE_RATE);
    }
    
    // Enhanced validation checks
    if (isNaN(amount) || amount <= 0) {
      setSwapError('Please enter a valid amount');
      setShowConfirmation(false);
      return;
    }
    
    if (isNaN(toAmountNum) || toAmountNum <= 0) {
      setSwapError('Invalid calculated amount to receive');
      setShowConfirmation(false);
      return;
    }

    // Additional security checks
    const SWAP_FEE_RATE = 0.001; // 0.1% fee
    const MAX_SLIPPAGE = 0.05; // 5% maximum slippage allowed
    const MIN_SWAP_AMOUNT = 0.0001; // Minimum swap amount
    const MAX_SWAP_AMOUNT = 1000000; // Maximum swap amount in USD value

    // Check minimum swap amount
    if (amount < MIN_SWAP_AMOUNT) {
      setSwapError(`Minimum swap amount is ${MIN_SWAP_AMOUNT}`);
      setShowConfirmation(false);
      return;
    }

    // Calculate USD value of the swap to check maximum
    const fromUsdValue = amount * (fromCurrency.price || 0);
    if (fromUsdValue > MAX_SWAP_AMOUNT) {
      setSwapError(`Maximum swap amount is $${MAX_SWAP_AMOUNT.toLocaleString()}`);
      setShowConfirmation(false);
      return;
    }


    // Double-check balance before executing
    if (fromCurrency.balance && amount > fromCurrency.balance) {
      setSwapError(`Insufficient ${fromCurrency.symbol} balance`);
      setShowConfirmation(false);
      return;
    }

    // Prevent swapping to the same currency
    if (fromCurrency.symbol === toCurrency.symbol) {
      setSwapError('Cannot swap to the same currency');
      setShowConfirmation(false);
      return;
    }

    // Check if prices are valid and not zero
    if (!fromCurrency.price || !toCurrency.price || fromCurrency.price <= 0 || toCurrency.price <= 0) {
      setSwapError('Invalid currency prices. Please try again later.');
      setShowConfirmation(false);
      return;
    }
    
    setIsSwapping(true);
    setSwapError(null);
    setSwapSuccess(null);
    
    try {
      console.log('Executing swap with params:', {
        fromCurrency: fromCurrency.symbol,
        toCurrency: toCurrency.symbol,
        amount,
        toAmountNum
      });

      // Call the swap function with the correct parameters
      const success = await onSwap(fromCurrency.symbol, toCurrency.symbol, amount, toAmountNum);

      console.log('Swap result:', success);

      if (success) {
        setSwapSuccess(`Successfully swapped ${amount} ${fromCurrency.symbol} to ${toAmountNum.toFixed(6)} ${toCurrency.symbol}`);
        // Reset form
        setFromAmount('');
        setToAmount('');
        setCalculatedToAmountFullPrecision(null);
        setCalculatedFromAmountFullPrecision(null);

        // Refresh transactions to update the UI
        if (fetchTransactions) {
          await fetchTransactions();
        }
      } else {
        throw new Error('Swap returned false without throwing an error');
      }
    } catch (error: any) {
      console.error('Swap error in component:', error);
      const errorMessage = error?.message || 'Swap failed. Please try again.';
      setSwapError(errorMessage);
    } finally {
      setIsSwapping(false);
      setShowConfirmation(false);
    }
  };

  // Calculate fees
  const calculateFee = () => {
    const amount = parseFloat(fromAmount) || 0;
    return amount * 0.001; // 0.1% fee
  };

  // Render crypto icon with fallback
  const renderCryptoIcon = (currency: CryptoCurrency) => {
    return (
      <div
        className="relative w-8 h-8 overflow-hidden flex items-center justify-center "
        style={{
          borderColor: Editorial.Ink,
          borderRadius: BorderRadius.None
        }}
      >
        <img
          src={currency.iconUrl}
          alt={currency.symbol}
          className="w-full h-full object-contain"
          onError={(e) => {
            // On error, replace with text icon
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
        <div
          className="absolute inset-0 hidden flex items-center justify-center font-bold text-sm"
          style={{ color: Editorial.Ink }}
        >
          {currency.symbol.substring(0, 2)}
        </div>
      </div>
    );
  };

  // Get market prices sorted by market cap
  const getSortedMarketPrices = () => {
    // Filter market data to only include allowed symbols
    const allowedSymbols = ALLOWED_SWAP_SYMBOLS.map(item => `${item.symbol}USDT`);
    
    // Filter valid market data for allowed symbols
    const validMarketData = marketData.filter(data => {
      if (data.price <= 0 || data.price === 104325.3716) return false;
      return allowedSymbols.includes(data.symbol);
    });
    
    // Sort by symbol alphabetically and take top 5
    return validMarketData.sort((a, b) => {
      return a.symbol.localeCompare(b.symbol);
    }).slice(0, 5);
  };

  // Filter transactions to only show swaps
  const swapTransactions = transactions.filter(tx => 
    tx.type === 'trade' && 
    tx.description.toLowerCase().includes('swap')
  );

  return (
    <div className="p-8 bg-slate-50/95">
      {/* Page Header */}
      <div className="mb-8 md:mb-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl"
          style={{
            fontWeight: Typography.WeightBold,
            color: '#1e293b',
            letterSpacing: '-0.04em'
          }}
        >
          {t('swap.title')}
        </h1>
        <div
          className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm"
          style={{ color: '#000000' }}
        >
          <div className="flex items-center gap-1.5">
            <div
              className="w-1.5 h-1.5"
              style={{ backgroundColor: '#d91222', borderRadius: '50%' }}
            ></div>
            <span>Live</span>
          </div>
          <button
            onClick={() => {
              refreshSnapshot();
            }}
            className="flex items-center gap-1 transition-colors"
            style={{ color: '#000000' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = Editorial.Red)}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#000000')}
            title="Refresh prices now"
          >
            <RefreshCw size={12} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* About Crypto Swaps - Full Width */}
      <div
        className="mb-6 md:mb-8 p-5 sm:p-6 md:p-8 border shadow-lg"
        style={{
          background: '#ffffff',
          borderColor: 'rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
        }}
      >
        <h3
          className="mb-5 pb-3"
          style={{
            fontSize: Typography.HeadlineSmall,
            fontWeight: Typography.WeightBold,
            color: '#1e293b',
            letterSpacing: '-0.02em',
            borderBottom: `1px solid rgba(0, 0, 0, 0.06)`
          }}
        >
          About Crypto Swaps
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          <div>
            <h4
              className="mb-3 flex items-center gap-2"
              style={{
                fontSize: Typography.BodyLarge,
                fontWeight: Typography.WeightBold,
                color: '#1e293b',
                textTransform: 'uppercase',
                letterSpacing: '0.08em'
              }}
            >
              <Zap size={18} style={{ color: Editorial.Red }} />
              What Are Crypto Swaps?
            </h4>
            <p
              className="mb-4 leading-relaxed"
              style={{
                color: '#000000',
                fontSize: Typography.BodySmall,
                lineHeight: '1.65'
              }}
            >
              Crypto swaps allow you to instantly exchange one cryptocurrency for another without going through traditional order books. Simply select your assets, enter the amount, and execute your trade at real-time market prices.
            </p>
            <p
              className="leading-relaxed"
              style={{
                color: '#000000',
                fontSize: Typography.BodySmall,
                lineHeight: '1.65'
              }}
            >
              Our platform supports 20+ major cryptocurrencies including Bitcoin, Ethereum, and popular altcoins, providing seamless conversion between any supported pair.
            </p>
          </div>

          <div>
            <h4
              className="mb-3 flex items-center gap-2"
              style={{
                fontSize: Typography.BodyLarge,
                fontWeight: Typography.WeightBold,
                color: '#1e293b',
                textTransform: 'uppercase',
                letterSpacing: '0.08em'
              }}
            >
              <Shield size={18} style={{ color: Editorial.Red }} />
              Key Advantages
            </h4>
            <div className="space-y-2">
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                <span style={{ fontWeight: Typography.WeightBold }}>Instant Execution:</span> Trades complete in seconds
              </div>
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                <span style={{ fontWeight: Typography.WeightBold }}>No Waiting:</span> Skip the order book entirely
              </div>
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                <span style={{ fontWeight: Typography.WeightBold }}>Simple Interface:</span> Easy to use for beginners
              </div>
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                <span style={{ fontWeight: Typography.WeightBold }}>Real-Time Prices:</span> Live market rates updated continuously
              </div>
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                <span style={{ fontWeight: Typography.WeightBold }}>24/7 Availability:</span> Swap anytime, anywhere
              </div>
            </div>
          </div>

          <div>
            <h4
              className="mb-3 flex items-center gap-2"
              style={{
                fontSize: Typography.BodyLarge,
                fontWeight: Typography.WeightBold,
                color: '#1e293b',
                textTransform: 'uppercase',
                letterSpacing: '0.08em'
              }}
            >
              <DollarSign size={18} style={{ color: Editorial.Red }} />
              Transparent Pricing
            </h4>
            <div className="space-y-2 mb-4">
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                <span style={{ fontWeight: Typography.WeightBold, color: Editorial.Red }}>Fixed 0.1% Fee</span>
              </div>
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                No gas fees
              </div>
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                No network fees
              </div>
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                No hidden costs
              </div>
              <div style={{ color: '#000000', fontSize: Typography.BodySmall }}>
                No minimum amounts
              </div>
            </div>
            <p
              className="leading-relaxed"
              style={{
                color: '#000000',
                fontSize: Typography.BodySmall,
                lineHeight: '1.65'
              }}
            >
              What you see is what you get. All fees are calculated upfront and displayed clearly before you confirm your swap.
            </p>
          </div>
        </div>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {/* Left Column - Main Swap Interface */}
        <div className="order-1">
          <div
            className="border shadow-lg overflow-hidden relative h-full flex flex-col"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderColor: 'rgba(0, 0, 0, 0.06)',
              borderRadius: '20px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            {/* Accent Bar */}
            <div
              className="h-1.5"
              style={{
                background: 'linear-gradient(90deg, #10b981 0%, #059669 50%, #d91222 100%)'
              }}
            />

            {/* Header */}
            <div className="p-6 pb-4 border-b" style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}>
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '14px',
                    background: 'linear-gradient(135deg, #10b981 0%, #d91222 100%)',
                    boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  <ArrowRightLeft size={24} style={{ color: '#ffffff' }} />
                </div>
                <div>
                  <h2
                    style={{
                      fontSize: Typography.HeadlineMedium,
                      fontWeight: Typography.WeightBold,
                      color: '#1e293b',
                      letterSpacing: '-0.02em'
                    }}
                  >
                    Swap Crypto
                  </h2>
                  <p style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.5)' }}>
                    Trade your assets instantly
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 md:p-10 lg:p-12 flex-1">

            {/* Status Messages */}
            {swapError && (
              <div
                className="mb-8 pb-4 flex items-center gap-3"
                style={{
                  borderBottom: `1px solid rgba(0, 0, 0, 0.1)`
                }}
              >
                <AlertTriangle size={16} style={{ color: Editorial.Red }} className="flex-shrink-0" />
                <span style={{ color: Editorial.Red, fontSize: Typography.BodySmall }}>{swapError}</span>
              </div>
            )}

            {swapSuccess && (
              <div
                className="mb-8 pb-4 flex items-center gap-3"
                style={{
                  borderBottom: `1px solid rgba(0, 0, 0, 0.1)`
                }}
              >
                <CheckCircle size={16} style={{ color: '#22c55e' }} className="flex-shrink-0" />
                <span style={{ color: '#22c55e', fontSize: Typography.BodySmall }}>{swapSuccess}</span>
              </div>
            )}

          {/* From Section */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.3)'
                  }}
                >
                  <TrendingUp size={12} style={{ color: '#10b981' }} />
                </div>
                <span
                  style={{
                    fontSize: Typography.Caption,
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontWeight: Typography.WeightBold,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em'
                  }}
                >
                  {t('swap.from')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign size={12} style={{ color: 'rgba(0, 0, 0, 0.5)' }} />
                <span
                  className="font-mono"
                  style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.7)' }}
                  translate="no"
                >
                  {t('common.balance')}: {fromCurrency.balance?.toFixed(fromCurrency.symbol === 'USDT' ? 2 : 6)} {fromCurrency.symbol}
                </span>
              </div>
            </div>
            <div
              className="p-5 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.01) 100%)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4">
                <input
                  type="text"
                  value={fromAmount}
                  onChange={(e) => handleFromAmountChange(e.target.value)}
                  placeholder={t('swap.enterAmount')}
                  className="bg-transparent font-mono text-lg sm:text-xl lg:text-2xl font-bold focus:outline-none w-full sm:flex-1"
                  style={{
                    color: '#000000',
                    fontFamily: Typography.FontMono
                  }}
                  translate="no"
                />
                <div className="relative" ref={fromDropdownRef}>
                  <button
                    onClick={() => setShowFromTokens(!showFromTokens)}
                    className="flex items-center gap-3 transition-colors"
                    style={{
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      const text = e.currentTarget.querySelector('span');
                      if (text) (text as HTMLElement).style.color = Editorial.Red;
                    }}
                    onMouseLeave={(e) => {
                      const text = e.currentTarget.querySelector('span');
                      if (text) (text as HTMLElement).style.color = '#000000';
                    }}
                  >
                    {renderCryptoIcon(fromCurrency)}
                    <span style={{ color: '#000000', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>
                      {fromCurrency.symbol}
                    </span>
                    <ChevronDown size={20} style={{ color: 'rgba(0, 0, 0, 0.7)' }} />
                  </button>


                  {/* From Token Dropdown */}
                  {showFromTokens && (
                    <div
                      className="absolute top-full right-0 mt-2 w-80 z-50 max-h-96 overflow-hidden"
                      style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
                      }}
                    >
                      <div className="p-4" style={{ borderBottom: `1px solid rgba(0, 0, 0, 0.08)` }}>
                        <div className="relative">
                          <Search
                            size={14}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2"
                            style={{ color: 'rgba(0, 0, 0, 0.5)' }}
                          />
                          <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 focus:outline-none"
                            style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.03)',
                              color: '#1a1a1a',
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              borderRadius: '10px',
                              fontSize: Typography.BodySmall
                            }}
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {getFilteredTokens(searchTerm).map((currency, index) => (
                          <button
                            key={currency.symbol}
                            onClick={() => handleSelectFromCurrency(currency)}
                            className="w-full flex items-center justify-between px-4 py-3 transition-colors"
                            style={{
                              borderBottom: index < getFilteredTokens(searchTerm).length - 1 ? `1px solid ${Editorial.RuleFaint}` : 'none'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div className="flex items-center gap-3">
                              {renderCryptoIcon(currency)}
                              <div className="text-left">
                                <div style={{ color: '#1a1a1a', fontWeight: Typography.WeightMedium, fontSize: Typography.BodyMedium }}>
                                  {currency.symbol}
                                </div>
                                <div style={{ color: '#4a4a4a', fontSize: Typography.Fine }}>
                                  {currency.name}
                                </div>
                              </div>
                            </div>
                            <div className="text-right font-mono" translate="no">
                              <div style={{ color: '#1a1a1a', fontSize: Typography.BodySmall }}>
                                {currency.balance?.toFixed(currency.symbol === 'USDT' ? 2 : 6) || '0'}
                              </div>
                              <div style={{ color: '#4a4a4a', fontSize: Typography.Fine }}>
                                ${(currency.price || 0).toFixed(2)}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center mt-4">
                <div className="flex items-center gap-2">
                  <span style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: Typography.Fine }}>≈</span>
                  <span
                    className="font-mono"
                    style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }}
                    translate="no"
                  >
                    ${((parseFloat(fromAmount) || 0) * (fromCurrency.price || 0)).toFixed(2)}
                  </span>
                </div>
                <button
                  onClick={handleMaxClick}
                  className="font-bold transition-all px-4 py-2 rounded-lg"
                  style={{
                    color: '#10b981',
                    fontSize: Typography.Caption,
                    letterSpacing: '0.08em',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#10b981';
                    e.currentTarget.style.color = '#ffffff';
                    e.currentTarget.style.borderColor = '#10b981';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
                    e.currentTarget.style.color = '#10b981';
                    e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                  }}
                >
                  MAX
                </button>
              </div>
            </div>
          </div>

          {/* Swap Direction Button */}
          <div className="flex justify-center my-8 sm:my-10 lg:my-12 relative">
            <div
              className="absolute inset-x-0 top-1/2 h-px"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.1) 50%, transparent 100%)'
              }}
            />
            <button
              onClick={handleSwapCurrencies}
              className="transition-all p-3 rounded-xl relative z-10"
              style={{
                backgroundColor: '#ffffff',
                border: '2px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = Editorial.Red;
                e.currentTarget.style.borderColor = Editorial.Red;
                e.currentTarget.style.transform = 'rotate(180deg) scale(1.1)';
                const icon = e.currentTarget.querySelector('svg');
                if (icon) (icon as HTMLElement).style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'rotate(0deg) scale(1)';
                const icon = e.currentTarget.querySelector('svg');
                if (icon) (icon as HTMLElement).style.color = 'rgba(0, 0, 0, 0.7)';
              }}
            >
              <ArrowDown className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: 'rgba(0, 0, 0, 0.7)' }} />
            </button>
          </div>

          {/* To Section */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15) 0%, rgba(217, 18, 34, 0.05) 100%)',
                    border: '1px solid rgba(217, 18, 34, 0.3)'
                  }}
                >
                  <TrendingDown size={12} style={{ color: Editorial.Red }} />
                </div>
                <span
                  style={{
                    fontSize: Typography.Caption,
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontWeight: Typography.WeightBold,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em'
                  }}
                >
                  {t('swap.to')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign size={12} style={{ color: 'rgba(0, 0, 0, 0.5)' }} />
                <span
                  className="font-mono"
                  style={{ fontSize: Typography.Fine, color: 'rgba(0, 0, 0, 0.7)' }}
                  translate="no"
                >
                  {t('common.balance')}: {toCurrency.balance?.toFixed(toCurrency.symbol === 'USDT' ? 2 : 6)} {toCurrency.symbol}
                </span>
              </div>
            </div>
            <div
              className="p-5 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.01) 100%)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4">
                <input
                  type="text"
                  value={toAmount}
                  onChange={(e) => handleToAmountChange(e.target.value)}
                  placeholder="0.00"
                  className="bg-transparent font-mono text-lg sm:text-xl lg:text-2xl font-bold focus:outline-none w-full sm:flex-1"
                  style={{
                    color: '#000000',
                    fontFamily: Typography.FontMono
                  }}
                  translate="no"
                />
                <div className="relative" ref={toDropdownRef}>
                  <button
                    onClick={() => setShowToTokens(!showToTokens)}
                    className="flex items-center gap-3 transition-colors"
                    style={{
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      const text = e.currentTarget.querySelector('span');
                      if (text) (text as HTMLElement).style.color = Editorial.Red;
                    }}
                    onMouseLeave={(e) => {
                      const text = e.currentTarget.querySelector('span');
                      if (text) (text as HTMLElement).style.color = '#000000';
                    }}
                  >
                    {renderCryptoIcon(toCurrency)}
                    <span style={{ color: '#000000', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>
                      {toCurrency.symbol}
                    </span>
                    <ChevronDown size={20} style={{ color: 'rgba(0, 0, 0, 0.7)' }} />
                  </button>


                  {/* To Token Dropdown */}
                  {showToTokens && (
                    <div
                      className="absolute top-full right-0 mt-2 w-80 z-50 max-h-96 overflow-hidden"
                      style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
                      }}
                    >
                      <div className="p-4" style={{ borderBottom: `1px solid rgba(0, 0, 0, 0.08)` }}>
                        <div className="relative">
                          <Search
                            size={14}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2"
                            style={{ color: 'rgba(0, 0, 0, 0.5)' }}
                          />
                          <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 focus:outline-none"
                            style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.03)',
                              color: '#1a1a1a',
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              borderRadius: '10px',
                              fontSize: Typography.BodySmall
                            }}
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {getFilteredTokens(searchTerm).map((currency, index) => (
                          <button
                            key={currency.symbol}
                            onClick={() => handleSelectToCurrency(currency)}
                            className="w-full flex items-center justify-between px-4 py-3 transition-colors"
                            style={{
                              borderBottom: index < getFilteredTokens(searchTerm).length - 1 ? `1px solid ${Editorial.RuleFaint}` : 'none'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div className="flex items-center gap-3">
                              {renderCryptoIcon(currency)}
                              <div className="text-left">
                                <div style={{ color: '#1a1a1a', fontWeight: Typography.WeightMedium, fontSize: Typography.BodyMedium }}>
                                  {currency.symbol}
                                </div>
                                <div style={{ color: '#4a4a4a', fontSize: Typography.Fine }}>
                                  {currency.name}
                                </div>
                              </div>
                            </div>
                            <div className="text-right font-mono" translate="no">
                              <div style={{ color: '#1a1a1a', fontSize: Typography.BodySmall }}>
                                {currency.balance?.toFixed(currency.symbol === 'USDT' ? 2 : 6) || '0'}
                              </div>
                              <div style={{ color: '#4a4a4a', fontSize: Typography.Fine }}>
                                ${(currency.price || 0).toFixed(2)}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <span style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: Typography.Fine }}>≈</span>
                <span
                  className="font-mono"
                  style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }}
                  translate="no"
                >
                  ${((parseFloat(toAmount) || 0) * (toCurrency.price || 0)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Swap Details */}
          {fromAmount && toAmount && (
            <div
              className="p-5 mb-12 space-y-4 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.01) 100%)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <div className="flex items-center gap-2 mb-3 pb-3" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Info size={14} style={{ color: 'rgba(0, 0, 0, 0.5)' }} />
                <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.Caption, fontWeight: Typography.WeightBold, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Swap Details
                </span>
              </div>

              <div className="flex justify-between items-center p-3 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                <div className="flex items-center gap-2">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%)',
                      border: '1px solid rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    <RefreshCw size={10} style={{ color: '#3b82f6' }} />
                  </div>
                  <span style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: Typography.BodySmall, fontWeight: Typography.WeightMedium }}>Exchange Rate</span>
                </div>
                <span className="font-mono" style={{ color: '#000000', fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }} translate="no">
                  1 {fromCurrency.symbol} = {((fromCurrency.price || 1) / (toCurrency.price || 1)).toFixed(8)} {toCurrency.symbol}
                </span>
              </div>

              <div className="flex justify-between items-center p-3 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                <div className="flex items-center gap-2">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.15) 0%, rgba(217, 18, 34, 0.05) 100%)',
                      border: '1px solid rgba(217, 18, 34, 0.3)'
                    }}
                  >
                    <Zap size={10} style={{ color: Editorial.Red }} />
                  </div>
                  <span style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: Typography.BodySmall, fontWeight: Typography.WeightMedium }}>
                    Fee
                  </span>
                </div>
                <span className="font-mono" style={{ color: Editorial.Red, fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }} translate="no">
                  {calculateFee().toFixed(6)} {fromCurrency.symbol} <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: Typography.WeightMedium, fontSize: Typography.Fine }}>(0.1%)</span>
                </span>
              </div>

              <div className="flex justify-between items-center p-3 rounded-lg transition-colors" style={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                <div className="flex items-center gap-2">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.3)'
                    }}
                  >
                    <Shield size={10} style={{ color: '#10b981' }} />
                  </div>
                  <span style={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: Typography.BodySmall, fontWeight: Typography.WeightMedium }}>Minimum Received</span>
                </div>
                <span className="font-mono" style={{ color: '#000000', fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }} translate="no">
                  {(parseFloat(toAmount) * 0.995).toFixed(6)} {toCurrency.symbol}
                </span>
              </div>
            </div>
          )}

          {/* Price Status Indicator */}
          {fromAmount && (!fromCurrency.price || !toCurrency.price) && (
            <div
              className="mb-8 pb-4 flex items-center gap-3"
              style={{
                borderBottom: `1px solid ${Editorial.Red}`,
                fontSize: Typography.BodySmall
              }}
            >
              <AlertTriangle size={16} style={{ color: Editorial.Red }} className="flex-shrink-0" />
              <span style={{ color: Editorial.Red }}>
                Price data unavailable. Click the Refresh button to update prices.
              </span>
            </div>
          )}

            {/* Swap Button */}
            <button
              onClick={handleShowConfirmation}
              disabled={!fromAmount || !toAmount || isSwapping || parseFloat(fromAmount) <= 0 || !fromCurrency.price || !toCurrency.price}
              className="w-full font-bold py-5 transition-all disabled:cursor-not-allowed border"
              style={{
                backgroundColor: 'transparent',
                borderColor: !fromAmount || !toAmount || isSwapping || parseFloat(fromAmount) <= 0 || !fromCurrency.price || !toCurrency.price
                  ? `rgba(0, 0, 0, 0.3)`
                  : Editorial.Red,
                color: !fromAmount || !toAmount || isSwapping || parseFloat(fromAmount) <= 0 || !fromCurrency.price || !toCurrency.price
                  ? `rgba(0, 0, 0, 0.3)`
                  : Editorial.Red,
                borderRadius: '8px',
                fontSize: Typography.HeadlineMedium,
                letterSpacing: '0.01em'
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = Editorial.Red;
                  e.currentTarget.style.color = '#ffffff';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = Editorial.Red;
                }
              }}
            >
              {isSwapping ? 'Swapping...' : !fromCurrency.price || !toCurrency.price ? 'Price Unavailable' : 'Swap'}
            </button>
            </div>
          </div>
        </div>

        {/* Right Column - Markets & Recent Swaps */}
        <div className="order-2 space-y-6 md:space-y-8">
          {/* Market Overview */}
          <div
            className="border shadow-lg overflow-hidden relative"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderColor: 'rgba(0, 0, 0, 0.08)',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            {/* Accent Bar */}
            <div
              className="h-1"
              style={{
                background: 'linear-gradient(90deg, #d91222 0%, #ff4757 100%)'
              }}
            />

            <div className="p-5 sm:p-6 md:p-8">
              <div className="flex items-center justify-between mb-6 pb-3" style={{ borderBottom: `1px solid rgba(0, 0, 0, 0.06)` }}>
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #d91222 0%, #ff4757 100%)',
                      boxShadow: '0 4px 12px rgba(217, 18, 34, 0.3)'
                    }}
                  >
                    <TrendingUp size={20} style={{ color: '#ffffff' }} />
                  </div>
                  <h3
                    style={{
                      fontSize: Typography.HeadlineSmall,
                      fontWeight: Typography.WeightBold,
                      color: '#1e293b',
                      letterSpacing: '-0.02em'
                    }}
                  >
                    Markets
                  </h3>
                </div>
                <button
                  onClick={refreshSnapshot}
                  className="transition-all p-2 rounded-lg"
                  style={{
                    color: 'rgba(0, 0, 0, 0.7)',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = Editorial.Red;
                    e.currentTarget.style.backgroundColor = 'rgba(217, 18, 34, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <RefreshCw size={16} />
                </button>
              </div>

              <div className="space-y-2">
                {getSortedMarketPrices().map((data) => {
                  const symbol = data.symbol.replace('USDT', '');
                  return (
                    <div
                      key={data.symbol}
                      className="flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer"
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        border: '1px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(217, 18, 34, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(217, 18, 34, 0.2)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.02)';
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="flex items-center justify-center"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.1) 0%, rgba(255, 71, 87, 0.1) 100%)',
                            border: '1px solid rgba(217, 18, 34, 0.2)'
                          }}
                        >
                          <span style={{ color: Editorial.Red, fontWeight: Typography.WeightBold, fontSize: '12px' }}>
                            {symbol.charAt(0)}
                          </span>
                        </div>
                        <span style={{ color: '#000000', fontWeight: Typography.WeightBold, fontSize: Typography.BodySmall }}>
                          {symbol}
                        </span>
                      </div>
                      <div className="text-right font-mono" translate="no">
                        <div style={{ color: '#000000', fontSize: Typography.BodySmall, fontWeight: Typography.WeightBold }}>
                          ${data.price.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recent Swaps */}
          <div
            className="border shadow-lg overflow-hidden relative"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderColor: 'rgba(0, 0, 0, 0.08)',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            {/* Accent Bar */}
            <div
              className="h-1"
              style={{
                background: 'linear-gradient(90deg, #0ea5e9 0%, #3b82f6 100%)'
              }}
            />

            <div className="p-5 sm:p-6 md:p-8">
              <div className="flex items-center justify-between mb-6 pb-3" style={{ borderBottom: `1px solid rgba(0, 0, 0, 0.06)` }}>
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)',
                      boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)'
                    }}
                  >
                    <Clock size={20} style={{ color: '#ffffff' }} />
                  </div>
                  <h3
                    style={{
                      fontSize: Typography.HeadlineSmall,
                      fontWeight: Typography.WeightBold,
                      color: '#1e293b',
                      letterSpacing: '-0.02em'
                    }}
                  >
                    Recent Swaps
                  </h3>
                </div>
                <div
                  className="px-3 py-1 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                    border: '1px solid rgba(14, 165, 233, 0.2)'
                  }}
                >
                  <span style={{ color: '#0ea5e9', fontSize: Typography.Fine, fontWeight: Typography.WeightBold }}>
                    {swapTransactions.length}
                  </span>
                </div>
              </div>

              <div className="space-y-2 max-h-[600px] overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {swapTransactions.length > 0 ? (
                  swapTransactions.slice(0, 8).map((tx) => (
                    <div
                      key={tx.id}
                      className="p-4 border transition-all"
                      style={{
                        borderRadius: '12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        borderColor: 'rgba(0, 0, 0, 0.06)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(14, 165, 233, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.02)';
                        e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div
                            className="flex items-center justify-center"
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                              background: tx.amount > 0
                                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(74, 222, 128, 0.1) 100%)'
                                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(248, 113, 113, 0.1) 100%)',
                              border: tx.amount > 0
                                ? '1px solid rgba(34, 197, 94, 0.2)'
                                : '1px solid rgba(239, 68, 68, 0.2)'
                            }}
                          >
                            <ArrowRightLeft size={14} style={{ color: tx.amount > 0 ? '#22c55e' : '#ef4444' }} />
                          </div>
                          <span
                            style={{
                              color: '#000000',
                              fontSize: Typography.BodySmall,
                              fontWeight: Typography.WeightMedium
                            }}
                          >
                            {tx.description}
                          </span>
                        </div>
                        <span
                          className="font-mono"
                          style={{
                            fontSize: Typography.BodySmall,
                            color: tx.amount > 0 ? '#22c55e' : '#ef4444',
                            fontWeight: Typography.WeightBold
                          }}
                          translate="no"
                        >
                          {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(6)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 ml-11">
                        <Clock size={10} style={{ color: 'rgba(0, 0, 0, 0.4)' }} />
                        <span
                          style={{
                            fontSize: Typography.Fine,
                            color: 'rgba(0, 0, 0, 0.6)'
                          }}
                        >
                          {new Date(tx.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-16">
                    <div
                      className="flex items-center justify-center mx-auto mb-4"
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                        border: '1px solid rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <Package size={32} style={{ color: 'rgba(0, 0, 0, 0.3)' }} />
                    </div>
                    <p style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: Typography.BodySmall, fontWeight: Typography.WeightMedium }}>
                      No swap history yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
        >
          <div
            className="p-16 border max-w-md w-full"
            style={{
              backgroundColor: 'rgba(23, 23, 23, 0.98)',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '12px'
            }}
          >
            <div className="flex items-center justify-between mb-12">
              <h3
                style={{
                  fontSize: Typography.DisplayMedium,
                  fontWeight: Typography.WeightBold,
                  color: 'rgba(255, 255, 255, 0.95)',
                  letterSpacing: '-0.04em'
                }}
              >
                Confirm Swap
              </h3>
              <button
                onClick={() => setShowConfirmation(false)}
                className="transition-colors"
                style={{ color: 'rgba(255, 255, 255, 0.7)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = Editorial.Red)}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)')}
              >
                <X size={22} />
              </button>
            </div>

            <div className="space-y-10 mb-12">
              <div className="pb-6" style={{ borderBottom: `1px solid rgba(255, 255, 255, 0.1)` }}>
                <div className="mb-4" style={{ fontSize: Typography.Caption, color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: Typography.WeightBold }}>
                  You Pay
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {renderCryptoIcon(fromCurrency)}
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>
                      {fromCurrency.symbol}
                    </span>
                  </div>
                  <div className="text-right font-mono" translate="no">
                    <div style={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineLarge }}>
                      {parseFloat(fromAmount).toFixed(6)}
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: Typography.Fine }}>
                      ≈ ${((parseFloat(fromAmount) || 0) * (fromCurrency.price || 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowDown size={22} style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
              </div>

              <div className="pb-6" style={{ borderBottom: `1px solid rgba(255, 255, 255, 0.1)` }}>
                <div className="mb-4" style={{ fontSize: Typography.Caption, color: 'rgba(255, 255, 255, 0.6)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: Typography.WeightBold }}>
                  You Receive
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {renderCryptoIcon(toCurrency)}
                    <span style={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineSmall }}>
                      {toCurrency.symbol}
                    </span>
                  </div>
                  <div className="text-right font-mono" translate="no">
                    <div style={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: Typography.WeightBold, fontSize: Typography.HeadlineLarge }}>
                      {parseFloat(toAmount).toFixed(6)}
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: Typography.Fine }}>
                      ≈ ${((parseFloat(toAmount) || 0) * (toCurrency.price || 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <div className="flex justify-between">
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: Typography.BodySmall }}>Exchange Rate</span>
                  <span className="font-mono" style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: Typography.BodyMedium }} translate="no">
                    1 {fromCurrency.symbol} = {((fromCurrency.price || 1) / (toCurrency.price || 1)).toFixed(8)} {toCurrency.symbol}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: Typography.BodySmall }}>Fee</span>
                  <span className="font-mono" style={{ color: Editorial.Red, fontSize: Typography.BodyMedium, fontWeight: Typography.WeightBold }} translate="no">
                    {calculateFee().toFixed(6)} {fromCurrency.symbol}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="flex-1 font-bold py-4 transition-colors border"
                style={{
                  backgroundColor: 'transparent',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '8px',
                  fontSize: Typography.BodyMedium
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={executeSwap}
                disabled={isSwapping}
                className="flex-1 font-bold py-4 transition-all disabled:cursor-not-allowed border"
                style={{
                  backgroundColor: 'transparent',
                  borderColor: isSwapping ? `rgba(255, 255, 255, 0.2)` : Editorial.Red,
                  color: isSwapping ? `rgba(255, 255, 255, 0.4)` : Editorial.Red,
                  borderRadius: '8px',
                  fontSize: Typography.BodyMedium
                }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = Editorial.Red;
                    e.currentTarget.style.color = '#ffffff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = Editorial.Red;
                  }
                }}
              >
                {isSwapping ? 'Swapping...' : 'Confirm Swap'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SwapCryptoPage;